import { useQuery } from '@apollo/client/react';
import { Box, Table, TableContainer, TableRow, TableBody } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AddButton } from 'common/components/Button/AddButton';
import { OpenIdConnectionsDocument } from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { Divider } from 'common/components/Divider';
import { TableCell } from 'common/components/Table';
import { Chip, ChipGroup } from 'common/components/Chip';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';

export const OpenIdManager = () => {
    const [translate] = useTranslation();
    const { data, loading } = useQuery(OpenIdConnectionsDocument);
    const { openGlobalDrawer } = useGlobalDrawer();

    const { openIdConnections } = data || {};

    const hasOpenIdConnections = !!openIdConnections?.length;

    return (
        <Box>
            {!loading && (
                <AddButton
                    onClick={() => {
                        openGlobalDrawer({
                            type: IGlobalDrawerType.OpenIdCreate,
                        });
                    }}
                >
                    {translate('openIdConnectionForm.openIdConnect')}
                </AddButton>
            )}
            {loading && (
                <Box sx={{ position: 'relative', height: '200px' }}>
                    <Loader />
                </Box>
            )}
            {hasOpenIdConnections && (
                <>
                    <Box sx={{ mt: 2 }}>
                        <Divider />
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableBody>
                                {openIdConnections.map((openIdConnection) => {
                                    const {
                                        id,
                                        name,
                                        active,
                                        numberOfUsers,
                                        clientId,
                                        clientSecret,
                                        authorizationEndpoint,
                                        tokenEndpoint,
                                        userEndpoint,
                                        scope,
                                        oidcOpJwksEndpoint,
                                    } = openIdConnection;

                                    const actionRequired =
                                        !clientId ||
                                        !clientSecret ||
                                        !authorizationEndpoint ||
                                        !tokenEndpoint ||
                                        !userEndpoint ||
                                        !scope ||
                                        !oidcOpJwksEndpoint;

                                    const hasChips = !active || actionRequired;

                                    const participantInfo = `${
                                        numberOfUsers || 0
                                    } ${translate(
                                        'participants'
                                    ).toLowerCase()}`;

                                    return (
                                        <TableRow
                                            key={`openIdConnection-${id}`}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                openGlobalDrawer({
                                                    type: IGlobalDrawerType.OpenIdUpdate,
                                                    itemId: id,
                                                })
                                            }
                                        >
                                            <TableCell>
                                                {name}

                                                <Box
                                                    sx={{
                                                        color: 'grey.500',
                                                        mt: '2px',
                                                    }}
                                                >
                                                    {participantInfo}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {hasChips && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'right',
                                                        }}
                                                    >
                                                        <Box sx={{ mr: 2 }}>
                                                            <ChipGroup>
                                                                {!active && (
                                                                    <Chip
                                                                        color="error"
                                                                        label={translate(
                                                                            'inactive'
                                                                        )}
                                                                    />
                                                                )}

                                                                {actionRequired && (
                                                                    <Chip
                                                                        color="error"
                                                                        label={translate(
                                                                            'actionRequired'
                                                                        )}
                                                                    />
                                                                )}
                                                            </ChipGroup>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};
